import { useEffect, useRef } from "react";
import { UserButton, useUser } from "@clerk/react";
import { Link, Outlet } from "react-router";
import * as Sentry from "@sentry/react";
import { Sun, Moon, Eye } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AccessDenied } from "@/components/access-denied";
import { tagClerkUser } from "@/lib/sentry";
import { LearnAccessProvider, useLearnAccess } from "@/lib/learn-access";
import { useProgramLeaderCheck } from "@/lib/use-program-leader-check";
import { useSessionExpiration } from "@/lib/use-session-expiration";
import { useTheme } from "@/lib/theme";

export function LayoutShell() {
  const check = useProgramLeaderCheck();
  const { user } = useUser();
  const { effective, setPreference } = useTheme();

  function handleThemeToggle() {
    setPreference(effective === "dark" ? "light" : "dark");
  }

  // Watch for session expiry and toast + redirect.
  useSessionExpiration();

  // Tag authenticated user in Sentry for error context.
  useEffect(() => {
    if (check.status === "authorized" && user) {
      tagClerkUser(user);
    }
  }, [check.status, user]);

  // Capture unauthorized sign-in attempt at most once per forbidden session.
  // useUser returns a new reference per render, so without the ref guard
  // the effect would re-fire and spam Sentry.
  const unauthCaptured = useRef(false);
  useEffect(() => {
    if (check.status === "forbidden" && user && !unauthCaptured.current) {
      tagClerkUser(user);
      Sentry.captureMessage("Unauthorized signin", { level: "info" });
      unauthCaptured.current = true;
    }
    if (check.status !== "forbidden") {
      unauthCaptured.current = false;
    }
  }, [check.status, user]);

  if (check.status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <Skeleton className="h-12 w-48" />
      </div>
    );
  }

  if (check.status === "forbidden") {
    return <AccessDenied />;
  }

  return (
    <LearnAccessProvider
      readOnly={check.readOnly}
      canPreview={check.superadmin}
    >
      <Chrome
        effective={effective}
        onThemeToggle={handleThemeToggle}
      />
    </LearnAccessProvider>
  );
}

/**
 * Header + preview banner + routed content.
 *
 * Split out of LayoutShell purely so it sits INSIDE LearnAccessProvider and can
 * call useLearnAccess — the preview toggle has to read the same context the
 * write surfaces read, or the two disagree about whether a preview is running.
 */
function Chrome({
  effective,
  onThemeToggle,
}: {
  effective: string;
  onThemeToggle: () => void;
}) {
  const { readOnly, actualReadOnly, canPreview, previewing, setPreviewing } =
    useLearnAccess();

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b">
        <div className="mx-auto flex h-14 max-w-screen-xl items-center justify-between px-4">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm font-bold tracking-tight hover:opacity-80 transition-opacity"
            aria-label="Avenue Learn home"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-6 w-6 rounded-sm bg-primary text-primary-foreground"
              aria-hidden="true"
            >
              <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize="14" fontWeight="700" fill="currentColor">A</text>
            </svg>
            {/* The wordmark is the first thing to go on a narrow header — the
                logo tile still carries the identity, and the preview controls
                need the room more than the word does. */}
            <span className="hidden sm:inline">Avenue Learn</span>
          </Link>
          <div className="flex items-center gap-2">
            {/* Read-only viewers see no attendance checkboxes, no notes editors
                and no "Add attendee" button. Without this badge that reads as a
                broken page rather than as their access level. Visible at every
                width — a phone is where the missing controls are least obvious. */}
            {readOnly && (
              <Badge
                variant="secondary"
                title={
                  previewing
                    ? "Previewing the read-only view"
                    : "You have read-only access to Avenue Learn"
                }
              >
                Read-only
              </Badge>
            )}
            {/* Superadmin-only, and hidden for an account that is ALREADY
                read-only — previewing a view you are permanently in is a control
                that can only confuse. */}
            {canPreview && !actualReadOnly && (
              <Button
                variant={previewing ? "default" : "ghost"}
                size="icon"
                // size="icon" is size-8 (32px) — under the 44px touch minimum.
                // Both classes live in className so tailwind-merge resolves them
                // against the data-size variant predictably; the md: modifier is
                // what lets the desktop size win back.
                className="size-11 md:size-8"
                onClick={() => setPreviewing(!previewing)}
                aria-pressed={previewing}
                aria-label={
                  previewing
                    ? "Exit read-only preview"
                    : "Preview the read-only view"
                }
                title={
                  previewing
                    ? "Exit read-only preview"
                    : "Preview the read-only view"
                }
              >
                <Eye className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              // Same 44px touch fix as the preview toggle beside it — these two
              // sit in one row and must not render at different sizes.
              className="size-11 md:size-8"
              onClick={onThemeToggle}
              aria-label={effective === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {effective === "dark" ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>
            <UserButton />
          </div>
        </div>
      </header>

      {/* Standing reminder, not a dismissible toast: the previewer's own
          server-side permissions are unchanged, so anything that did slip past
          the read-only flag would still write for real. It also stops a preview
          left running in another tab from reading as "my controls disappeared".
          Stacks vertically under sm so the exit button never overlaps the text. */}
      {previewing && (
        <div
          role="status"
          className="border-b border-primary/30 bg-primary/10 px-4 py-2"
        >
          <div className="mx-auto flex max-w-screen-xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm">
              <span className="font-semibold">Previewing read-only access.</span>{" "}
              This is what a viewer such as Alex sees. Your own permissions are
              unchanged.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="min-h-11 self-start md:min-h-7 sm:self-auto"
              onClick={() => setPreviewing(false)}
            >
              Exit preview
            </Button>
          </div>
        </div>
      )}

      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}
